import type { CreateEnquirySessionPayload } from 'api/accommodationBooking'
import { buildSessionLoyaltyUpgrade, getHotelOnlyPropertyRatePricing, getHotelPricing } from 'lib/accommodation/buildEnquirySessionPayload'
import { getAccommodationV2RateIdentity, mapPropertyRateRef } from 'lib/checkout/accommodation/cart'

function mapSupplierMetaData(
  rate: App.RawPropertyRate,
): CreateEnquirySessionPayload['supplierMetaData'] {
  if (rate.service.type === 'bedbank') {
    return {
      internalService: 'bedbank',
      selectedRate: {
        optionId: rate.optionId,
        internalRateId: rate.service.rateId,
        ...(rate.service.rateGroupId ? { rateGroupId: rate.service.rateGroupId } : {}),
        ...(rate.service.bedGroups[0]?.id ? { bedGroupId: rate.service.bedGroups[0].id } : {}),
      },
      internalPropertyId: rate.service.propertyId,
      internalRoomId: rate.service.roomId,
    }
  }

  return {
    internalService: 'reservation',
    selectedRate: {
      optionId: rate.optionId,
      internalRateId: rate.service.rateId,
      ...(rate.service.ratePlanId ? { ratePlanId: rate.service.ratePlanId } : {}),
      ...(rate.groupKey ? { rateGroupId: rate.groupKey } : {}),
    },
    internalPropertyId: rate.service.propertyId,
    internalRoomId: rate.service.roomId,
  }
}

/**
 * The replacement session's hotel pricing and channel markup claim. svc-accommodation prices the
 * enquiry `rate` un-marked, and sends the campaign it has now for the session, priced onto that
 * rate, as the enquiry item's `channelMarkup`. With it the session takes those marked prices and
 * claims that campaign; without it (no markup, or the campaign ended) the un-marked rate prices.
 */
function getReplacementPricing(
  rate: App.RawPropertyRate,
  channelMarkup: App.AccommodationCheckoutEnquiryChannelMarkup | undefined,
): {
  pricing: CreateEnquirySessionPayload['pricing']
  channelMarkup?: App.Checkout.AccommodationV2ChannelMarkup
} {
  const pricing = getHotelPricing(rate)
  if (!channelMarkup) return { pricing }

  // The enquiry prices replace the rate's. A member price is on that block whenever the live rate
  // has one: svc-accommodation passes the unmarked member sell through when the campaign has no
  // member term. Omitting it means the rate has no member price, and the claim then carries no
  // `baseLuxPlusTotal`. Sending a member price anyway is rejected, because the base and the
  // marked member price must both be present or both be absent.
  const { luxPlusTotal: _unmarkedLuxPlusTotal, ...unmarkedPricing } = pricing
  const { sellTotal, luxPlusTotal, ...claim } = channelMarkup
  return {
    pricing: {
      ...unmarkedPricing,
      sellTotal,
      ...(luxPlusTotal != null ? { luxPlusTotal } : {}),
    },
    channelMarkup: claim,
  }
}

/**
 * An applied upgrade's discount is the target's price above the source's. The target is the rate
 * being re-sessioned, so its pricing follows the live rate; the benefit payment does the same.
 */
function withLiveTargetPricing(
  upgrade: App.Checkout.PropertyRateLoyaltyUpgrade | undefined,
  rate: App.RawPropertyRate,
): App.Checkout.PropertyRateLoyaltyUpgrade | undefined {
  if (!upgrade || upgrade.applied === false) return upgrade
  return { ...upgrade, targetPricing: getHotelOnlyPropertyRatePricing(rate) }
}

/**
 * Points a cart item at its replacement session. Every field that add-to-cart derives from the rate
 * is refreshed from the live rate, so the item's snapshot matches the session it now names. The
 * pricing and channel markup claim are the replacement session payload's, from `getHotelPricing`
 * and the enquiry's `channelMarkup`.
 *
 * `rate` is a raw checkout-enquiry rate, not an enriched one:
 * - it has no availability session, so the shopped `accommAvailabilitySessionId` is kept;
 * - its totals carry no flight enrichment, so no flight overhead is taken off the sell total.
 *
 * Occupancy, dates, property, room and bundles stay as they are, as on the replacement session.
 * Loyalty upgrade metadata also stays, with an applied upgrade's target pricing refreshed.
 */
export function applyAccommodationV2ReplacementSession(
  item: App.Checkout.AccommodationV2Item,
  rate: App.RawPropertyRate,
  sessionId: string,
  /** The enquiry item's `channelMarkup`, as given to `buildReplacementEnquirySessionPayload`. */
  enquiryChannelMarkup?: App.AccommodationCheckoutEnquiryChannelMarkup,
): App.Checkout.AccommodationV2Item {
  const { pricing, channelMarkup } = getReplacementPricing(rate, enquiryChannelMarkup)

  return {
    ...item,
    // A restored cart brings back the superseded rate's svc-cart totals. Clear them: the svc-cart
    // save recomputes them from the item view, which prices from the new session.
    total: undefined,
    memberTotal: undefined,
    nonMemberTotal: undefined,
    sessionId,
    ...getAccommodationV2RateIdentity(rate),
    optionId: rate.optionId,
    sellTotal: pricing.sellTotal,
    currency: pricing.currency,
    // Assigned even when absent, so the superseded rate's values are cleared rather than kept.
    propertyFees: pricing.propertyFees,
    surchargePayableAtProperty: pricing.surchargePayableAtProperty,
    luxPlusTotal: pricing.luxPlusTotal,
    channelMarkup,
    paymentMethod: rate.paymentMethod.type,
    token: rate.token,
    isFlightBundle: rate.isFlightBundle,
    boardCode: rate.boardCode,
    accommCampaignId: rate.promotion?.isCampaign ? rate.promotion.campaignId : undefined,
    ...(item.propertyRateRef && {
      propertyRateRef: mapPropertyRateRef(rate, item.propertyRateRef.bundleId),
    }),
    loyaltyPropertyRateUpgrade: withLiveTargetPricing(item.loyaltyPropertyRateUpgrade, rate),
  }
}

/**
 * Rebuilds a session-create payload after a `price_changed` enquiry.
 * Pricing and rate identity come from the live rate; occupancy, dates, property,
 * room, and bundles stay on the existing session. LuxPlus+ eligibility and LPP member pricing
 * come from the caller, as for `buildEnquirySessionPayloadFromCartItem`, and price the loyalty
 * upgrade discount.
 *
 * The price token also comes from the live rate, and is omitted when that rate has none.
 * Never fall back to the existing session's token: the price change invalidated it.
 *
 * Channel markup follows the enquiry's `channelMarkup`, never the existing session's: a campaign
 * that ended or changed since is what moved the price.
 */
export function buildReplacementEnquirySessionPayload(
  session: App.AccommodationEnquirySession,
  rate: App.RawPropertyRate,
  isLuxPlusUser: boolean,
  lppMemberPricingEnabled: boolean,
  /** The item's loyalty package upgrade, so the replacement keeps carrying the discount. */
  loyaltyUpgrade?: App.Checkout.PropertyRateLoyaltyUpgrade,
  /** The enquiry item's `channelMarkup`: the campaign svc-promo has now for the session. */
  enquiryChannelMarkup?: App.AccommodationCheckoutEnquiryChannelMarkup,
): CreateEnquirySessionPayload {
  const { occupancy, property, room, checkIn, checkOut, region, bundle, optInBundles } = session.data
  const { pricing, channelMarkup } = getReplacementPricing(rate, enquiryChannelMarkup)

  return {
    pricing,
    property: { propertyId: property.propertyId },
    room: { roomId: room.roomId },
    occupancy,
    supplierMetaData: mapSupplierMetaData(rate),
    checkIn,
    checkOut,
    region,
    currency: rate.currency,
    ...(bundle ? { bundle } : {}),
    ...(optInBundles?.length ? { optInBundles } : {}),
    ...buildSessionLoyaltyUpgrade(withLiveTargetPricing(loyaltyUpgrade, rate), isLuxPlusUser, lppMemberPricingEnabled),
    ...(channelMarkup && { channelMarkup }),
  }
}
